import { useMutation, useQuery } from '@apollo/client/react';
import { type Editor } from '@tiptap/react';
import { PenIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  CREATE_REVIEW_QUESTION,
  type CreateReviewQuestionResponse,
  type DealOpportunityCommentaryInput,
  type DealOpportunityCommentaryQuestionCreateInput,
  type DealOpportunityCommentaryQuestionUpdateInput,
  DELETE_REVIEW_QUESTION,
  type DeleteDealOpportunityCommentaryQuestionInput,
  type DeleteReviewQuestionResponse,
  formatDate,
  GET_FEEDBACK,
  type GetFeedbackResponse,
  type ReviewQuestion,
  UPDATE_DEAL_OPPORTUNITY_REVIEW,
  UPDATE_REVIEW_QUESTION,
  type UpdateDealOpportunityReviewResponse,
  type UpdateReviewQuestionResponse
} from './graphql';

import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import RichTextField from '@/components/RichTextField';
import RichTextFieldReadOnly from '@/components/RichTextFieldReadOnly';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Empty, EmptyContent } from '@/components/ui/empty';
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/components/ui/field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';

export type ReportType = 'icb' | 'rsm';

export function StageFeedbackView({
  opportunityId,
  reportType
}: {
  opportunityId: string;
  reportType: ReportType;
}) {
  const { data, error, loading, refetch } = useQuery<
    GetFeedbackResponse,
    { id: string }
  >(GET_FEEDBACK, {
    variables: { id: opportunityId }
  });

  const [updateReview, { loading: isUpdatingReview }] = useMutation<
    UpdateDealOpportunityReviewResponse,
    { values: DealOpportunityCommentaryInput }
  >(UPDATE_DEAL_OPPORTUNITY_REVIEW);

  const [createQuestion, { loading: isCreatingQuestion }] = useMutation<
    CreateReviewQuestionResponse,
    { values: DealOpportunityCommentaryQuestionCreateInput }
  >(CREATE_REVIEW_QUESTION);

  const [updateQuestion, { loading: isUpdatingQuestion }] = useMutation<
    UpdateReviewQuestionResponse,
    { id: string; values: DealOpportunityCommentaryQuestionUpdateInput }
  >(UPDATE_REVIEW_QUESTION);

  const [deleteQuestion, { loading: isDeletingQuestion }] = useMutation<
    DeleteReviewQuestionResponse,
    { id: string; values: DeleteDealOpportunityCommentaryQuestionInput }
  >(DELETE_REVIEW_QUESTION);

  const rsmFeedback = data?.node?.rsmFeedbackId;
  const icbFeedback = data?.node?.icbFeedbackId;
  const currentFeedback = reportType === 'rsm' ? rsmFeedback : icbFeedback;
  const questions = currentFeedback?.reviewQuestionIds ?? [];

  const [isStageDialogOpen, setIsStageDialogOpen] = useState(false);
  const [isAddQuestionDialogOpen, setIsAddQuestionDialogOpen] = useState(false);
  const [isEditQuestionDialogOpen, setIsEditQuestionDialogOpen] =
    useState(false);
  const [isAnswerDialogOpen, setIsAnswerDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<null | ReviewQuestion>(
    null
  );

  const [stageDraft, setStageDraft] = useState<{ stage: ReportType }>({
    stage: reportType
  });
  const [questionDraft, setQuestionDraft] = useState({
    answer: '',
    question: ''
  });

  const assumptionsFieldRef = useRef<Editor>(null);
  const commentaryFieldRef = useRef<Editor>(null);

  useEffect(() => {
    setStageDraft({ stage: reportType });
  }, [reportType]);

  const handleSaveStage = async () => {
    if (!assumptionsFieldRef.current || !commentaryFieldRef.current) {
      toast.error('Unable to read stage feedback');
      return;
    }

    try {
      const { data: result } = await updateReview({
        variables: {
          values: {
            assumptions: assumptionsFieldRef.current.isEmpty
              ? undefined
              : assumptionsFieldRef.current.getHTML(),
            commentary: commentaryFieldRef.current.isEmpty
              ? undefined
              : commentaryFieldRef.current.getHTML(),
            opportunityId,
            stage: stageDraft.stage
          }
        }
      });

      if (result?.updateDealOpportunityReview.ok) {
        toast.success('Feedback updated successfully');
        setIsStageDialogOpen(false);
        void refetch();
      } else {
        toast.error(
          result?.updateDealOpportunityReview.errors?.[0] ||
            'Failed to update feedback'
        );
      }
    } catch {
      toast.error('Failed to update feedback');
    }
  };

  const handleAddQuestion = async () => {
    if (!questionDraft.question.trim()) {
      toast.error('Question is required');
      return;
    }

    try {
      const { data: result } = await createQuestion({
        variables: {
          values: {
            answer: questionDraft.answer || undefined,
            opportunityId,
            question: questionDraft.question,
            stage: reportType
          }
        }
      });

      if (result?.createDealOpportunityReviewQuestion.ok) {
        toast.success('Question added successfully');
        setIsAddQuestionDialogOpen(false);
        setQuestionDraft({ answer: '', question: '' });
        void refetch();
      } else {
        toast.error(
          result?.createDealOpportunityReviewQuestion.errors?.[0] ||
            'Failed to add question'
        );
      }
    } catch {
      toast.error('Failed to add question');
    }
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion || !questionDraft.question.trim()) {
      toast.error('Question is required');
      return;
    }

    try {
      const { data: result } = await updateQuestion({
        variables: {
          id: editingQuestion.id,
          values: {
            answer: questionDraft.answer || undefined,
            opportunityId,
            question: questionDraft.question,
            stage: reportType
          }
        }
      });

      if (result?.updateDealOpportunityReviewQuestion.ok) {
        toast.success('Question updated successfully');
        setIsEditQuestionDialogOpen(false);
        setEditingQuestion(null);
        setQuestionDraft({ answer: '', question: '' });
        void refetch();
      } else {
        toast.error(
          result?.updateDealOpportunityReviewQuestion.errors?.[0] ||
            'Failed to update question'
        );
      }
    } catch {
      toast.error('Failed to update question');
    }
  };

  const handleAnswerQuestion = async () => {
    if (!editingQuestion) return;

    try {
      const { data: result } = await updateQuestion({
        variables: {
          id: editingQuestion.id,
          values: {
            answer: questionDraft.answer || undefined,
            opportunityId,
            question: editingQuestion.question ?? '',
            stage: reportType
          }
        }
      });

      if (result?.updateDealOpportunityReviewQuestion.ok) {
        toast.success('Answer saved successfully');
        setIsAnswerDialogOpen(false);
        setEditingQuestion(null);
        setQuestionDraft({ answer: '', question: '' });
        void refetch();
      } else {
        toast.error(
          result?.updateDealOpportunityReviewQuestion.errors?.[0] ||
            'Failed to save answer'
        );
      }
    } catch {
      toast.error('Failed to save answer');
    }
  };

  const handleDeleteQuestion = async () => {
    if (!editingQuestion) return;

    try {
      const { data: result } = await deleteQuestion({
        variables: {
          id: editingQuestion.id,
          values: {
            opportunityId,
            stage: reportType
          }
        }
      });

      if (result?.deleteDealOpportunityReviewQuestion.ok) {
        toast.success('Question deleted successfully');
        setIsDeleteDialogOpen(false);
        setEditingQuestion(null);
        void refetch();
      } else {
        toast.error(
          result?.deleteDealOpportunityReviewQuestion.errors?.[0] ||
            'Failed to delete question'
        );
      }
    } catch {
      toast.error('Failed to delete question');
    }
  };

  const openEditQuestion = (question: ReviewQuestion) => {
    setEditingQuestion(question);
    setQuestionDraft({
      answer: question.answer ?? '',
      question: question.question ?? ''
    });
    setIsEditQuestionDialogOpen(true);
  };

  const openAnswerQuestion = (question: ReviewQuestion) => {
    setEditingQuestion(question);
    setQuestionDraft({
      answer: question.answer ?? '',
      question: question.question ?? ''
    });
    setIsAnswerDialogOpen(true);
  };

  const openDeleteQuestion = (question: ReviewQuestion) => {
    setEditingQuestion(question);
    setIsDeleteDialogOpen(true);
  };

  if (loading && !data) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500">
        Error loading feedback: {error.message}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Stage Feedback</h2>
        <Button
          onClick={() => {
            setStageDraft({
              stage: reportType
            });
            setIsStageDialogOpen(true);
          }}
          variant="ghost"
        >
          <PenIcon />
          Edit
        </Button>
      </div>
      <div className="bg-card border-border rounded-md border p-5">
        <div className="space-y-5">
          <div className="space-y-2">
            <div className="text-sm font-medium">
              Assumptions (Must Believes)
            </div>
            <RichTextFieldReadOnly html={currentFeedback?.assumptions ?? ''} />
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium">
              General Investment Committee Commentary
            </div>
            <RichTextFieldReadOnly html={currentFeedback?.commentary ?? ''} />
          </div>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">Questions & Answers</h2>
              {questions.length !== 0 && (
                <Button
                  onClick={() => {
                    setQuestionDraft({ answer: '', question: '' });
                    setIsAddQuestionDialogOpen(true);
                  }}
                >
                  <PlusIcon />
                  Add Question
                </Button>
              )}
            </div>
            <div className="bg-card border-border rounded-md border p-5">
              {questions.length === 0 ? (
                <Empty className="p-0!">
                  <EmptyContent>
                    <Button
                      onClick={() => {
                        setQuestionDraft({ answer: '', question: '' });
                        setIsAddQuestionDialogOpen(true);
                      }}
                    >
                      <PlusIcon />
                      Add Question
                    </Button>
                  </EmptyContent>
                </Empty>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold">
                        Question By
                      </TableHead>
                      <TableHead className="text-xs font-semibold">
                        Question
                      </TableHead>
                      <TableHead className="text-xs font-semibold">
                        Answer
                      </TableHead>
                      <TableHead className="text-xs font-semibold">
                        Date
                      </TableHead>
                      <TableHead className="w-[200px] text-xs font-semibold">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {questions.map(question => (
                      <TableRow key={question.id}>
                        <TableCell>{question.createUid?.name ?? '-'}</TableCell>
                        <TableCell className="max-w-[200px]">
                          <TruncatedTextTooltip className="max-w-[200px] block">
                            {question.question ?? '-'}
                          </TruncatedTextTooltip>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <TruncatedTextTooltip className="max-w-[200px] block">
                            {question.answer ?? '-'}
                          </TruncatedTextTooltip>
                        </TableCell>
                        <TableCell>{formatDate(question.createDate)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              onClick={() => {
                                openAnswerQuestion(question);
                              }}
                              size="sm"
                              variant="outline"
                            >
                              Answer
                            </Button>
                            <Button
                              onClick={() => {
                                openEditQuestion(question);
                              }}
                              size="icon-sm"
                              variant="ghost"
                            >
                              <PenIcon className="size-4" />
                            </Button>
                            <Button
                              onClick={() => {
                                openDeleteQuestion(question);
                              }}
                              size="icon-sm"
                              variant="ghost"
                            >
                              <TrashIcon className="size-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stage Edit Dialog */}
      <Dialog
        onOpenChange={setIsStageDialogOpen}
        open={isStageDialogOpen}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              Edit Stage Feedback
              <Badge variant="yellow">Unsaved</Badge>
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <FieldGroup>
              <FieldSet>
                <FieldLegend variant="label">Stage</FieldLegend>
                <RadioGroup
                  className="grid grid-cols-2 gap-x-6"
                  disabled
                  onValueChange={value => {
                    setStageDraft(prev => ({
                      ...prev,
                      stage: value as ReportType
                    }));
                  }}
                  value={stageDraft.stage}
                >
                  <Field
                    className="w-auto items-center gap-x-2 [&>[data-slot=field-label]]:flex-none"
                    orientation="horizontal"
                  >
                    <FieldLabel
                      className="leading-none font-normal"
                      htmlFor="rsm-edit"
                    >
                      RSM
                    </FieldLabel>
                    <RadioGroupItem
                      id="rsm-edit"
                      value="rsm"
                    />
                  </Field>
                  <Field
                    className="w-auto items-center gap-x-2 [&>[data-slot=field-label]]:flex-none"
                    orientation="horizontal"
                  >
                    <FieldLabel
                      className="leading-none font-normal"
                      htmlFor="icb-edit"
                    >
                      ICB
                    </FieldLabel>
                    <RadioGroupItem
                      id="icb-edit"
                      value="icb"
                    />
                  </Field>
                </RadioGroup>
              </FieldSet>
              <Field>
                <FieldLabel htmlFor="assumptions-field">
                  Assumptions (Must Believes)
                </FieldLabel>
                <RichTextField
                  defaultValue={currentFeedback?.assumptions ?? ''}
                  id="assumptions-field"
                  ref={assumptionsFieldRef}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="commentary-field">
                  General Investment Committee Commentary
                </FieldLabel>
                <RichTextField
                  defaultValue={currentFeedback?.commentary ?? ''}
                  id="commentary-field"
                  ref={commentaryFieldRef}
                />
              </Field>
            </FieldGroup>
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              disabled={isUpdatingReview}
              onClick={() => void handleSaveStage()}
            >
              {isUpdatingReview && <Spinner />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Question Dialog */}
      <Dialog
        onOpenChange={setIsAddQuestionDialogOpen}
        open={isAddQuestionDialogOpen}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              Add Question
              <Badge variant="yellow">Unsaved</Badge>
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="new-question-field">Question</FieldLabel>
                <Textarea
                  id="new-question-field"
                  onChange={e => {
                    setQuestionDraft(prev => ({
                      ...prev,
                      question: e.target.value
                    }));
                  }}
                  placeholder="Enter your question..."
                  value={questionDraft.question}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="new-answer-field">
                  Answer (optional)
                </FieldLabel>
                <Textarea
                  id="new-answer-field"
                  onChange={e => {
                    setQuestionDraft(prev => ({
                      ...prev,
                      answer: e.target.value
                    }));
                  }}
                  placeholder="Enter answer..."
                  value={questionDraft.answer}
                />
              </Field>
            </FieldGroup>
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              disabled={isCreatingQuestion}
              onClick={() => void handleAddQuestion()}
            >
              {isCreatingQuestion && <Spinner />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog
        onOpenChange={setIsEditQuestionDialogOpen}
        open={isEditQuestionDialogOpen}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              Edit Question
              <Badge variant="yellow">Unsaved</Badge>
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="edit-question-field">Question</FieldLabel>
                <Textarea
                  id="edit-question-field"
                  onChange={e => {
                    setQuestionDraft(prev => ({
                      ...prev,
                      question: e.target.value
                    }));
                  }}
                  placeholder="Enter your question..."
                  value={questionDraft.question}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="edit-answer-field">Answer</FieldLabel>
                <Textarea
                  id="edit-answer-field"
                  onChange={e => {
                    setQuestionDraft(prev => ({
                      ...prev,
                      answer: e.target.value
                    }));
                  }}
                  placeholder="Enter answer..."
                  value={questionDraft.answer}
                />
              </Field>
            </FieldGroup>
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              disabled={isUpdatingQuestion}
              onClick={() => void handleUpdateQuestion()}
            >
              {isUpdatingQuestion && <Spinner />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Answer Question Dialog */}
      <Dialog
        onOpenChange={setIsAnswerDialogOpen}
        open={isAnswerDialogOpen}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              Answer Question
              <Badge variant="yellow">Unsaved</Badge>
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <FieldGroup>
              <Field>
                <FieldLabel>Question</FieldLabel>
                <Textarea
                  className="text-muted-foreground"
                  readOnly
                  value={editingQuestion?.question ?? ''}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="answer-field">Your Answer</FieldLabel>
                <Textarea
                  id="answer-field"
                  onChange={e => {
                    setQuestionDraft(prev => ({
                      ...prev,
                      answer: e.target.value
                    }));
                  }}
                  placeholder="Enter your answer..."
                  value={questionDraft.answer}
                />
              </Field>
            </FieldGroup>
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              disabled={isUpdatingQuestion}
              onClick={() => void handleAnswerQuestion()}
            >
              {isUpdatingQuestion && <Spinner />}
              Save Answer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        onOpenChange={setIsDeleteDialogOpen}
        open={isDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this question? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeletingQuestion}
              onClick={e => {
                e.preventDefault();
                void handleDeleteQuestion();
              }}
            >
              {isDeletingQuestion && <Spinner />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
